'use client';
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Search, RefreshCw, Tag, ChevronDown, ChevronRight, Loader2, ExternalLink, Info, Plus, X } from 'lucide-react';
import { keywordService } from '@/lib/services/thesisService';

interface ElsevierKeyword {
  id: string;
  code: string;
  label: string;
  area: string;
  is_active: boolean;
  created_at: string;
}

const AREAS_STATIC = ['All Areas', 'Computer Science', 'Business & Economics', 'Law', 'Social Sciences', 'Education', 'Architecture', 'Engineering', 'Health Sciences', 'Tourism'];

export default function ElsevierKeywordsAdmin() {
  const [keywords, setKeywords] = useState<ElsevierKeyword[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [areaFilter, setAreaFilter] = useState('All Areas');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [expandedArea, setExpandedArea] = useState<string | null>('Computer Science');
  const [isSyncing, setIsSyncing] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newArea, setNewArea] = useState('');

  useEffect(() => {
    loadKeywords();
  }, []);

  const loadKeywords = async () => {
    setLoading(true);
    try {
      const data = await keywordService.getAll();
      setKeywords(data);
    } catch (err) {
      console.log('Load keywords error:', err);
    } finally {
      setLoading(false);
    }
  };

  const uniqueAreas = ['All Areas', ...Array.from(new Set(keywords.map(k => k.area).filter(Boolean)))];

  const filtered = keywords.filter(k => {
    const matchSearch = !search || k.label.toLowerCase().includes(search.toLowerCase()) || k.code.toLowerCase().includes(search.toLowerCase());
    const matchArea = areaFilter === 'All Areas' || k.area === areaFilter;
    const matchStatus = statusFilter === 'all' || (statusFilter === 'active' ? k.is_active : !k.is_active);
    return matchSearch && matchArea && matchStatus;
  });

  const groupedByArea = Array.from(new Set(keywords.map(k => k.area))).reduce<Record<string, ElsevierKeyword[]>>((acc, area) => {
    acc[area] = filtered.filter(k => k.area === area);
    return acc;
  }, {});

  const toggleActive = async (kw: ElsevierKeyword) => {
    const result = await keywordService.update(kw.id, { is_active: !kw.is_active });
    if (result) {
      setKeywords(prev => prev.map(k => k.id === kw.id ? { ...k, is_active: !k.is_active } : k));
      toast.success(`Keyword "${kw.label}" ${kw.is_active ? 'deactivated' : 'activated'}.`);
    } else {
      toast.error('Failed to update keyword.');
    }
  };

  const handleAddKeyword = async () => {
    if (!newCode.trim() || !newLabel.trim() || !newArea.trim()) {
      toast.error('Please fill in all fields.');
      return;
    }
    const result = await keywordService.create(newCode.trim().toUpperCase(), newLabel.trim(), newArea.trim());
    if (result) {
      setKeywords(prev => [...prev, result]);
      setNewCode('');
      setNewLabel('');
      setNewArea('');
      setShowAddForm(false);
      toast.success(`Keyword "${result.label}" added successfully.`);
    } else {
      toast.error('Failed to add keyword. Code may already exist.');
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const resp = await fetch('/api/cron/sync-elsevier-keywords', { method: 'POST' });
      const data = await resp.json().catch(() => null);
      if (!resp.ok || !data) {
        toast.error(data?.error || `Sync failed (HTTP ${resp.status})`);
      } else {
        const src = data.source === 'elsevier' ? 'Elsevier API' : 'local seed (set ELSEVIER_API_KEY for live sync)';
        toast.success(`Synced ${data.upserted} / ${data.total} from ${src}.`, { duration: 6000 });
        await loadKeywords();
      }
    } catch (err: any) {
      toast.error(`Sync failed: ${err?.message || 'Network error'}`);
    } finally {
      setIsSyncing(false);
    }
  };

  const activeCount = keywords.filter(k => k.is_active).length;
  const inactiveCount = keywords.filter(k => !k.is_active).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={28} className="animate-spin text-[#1a2e5a]" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-700 text-[#0f1a35]">Elsevier Subject Classifications</h2>
          <p className="text-sm text-[#4a5578] mt-0.5">
            Manage the keyword list available to students and librarians.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-2 border border-[#1a2e5a] text-[#1a2e5a] hover:bg-[#1a2e5a] hover:text-white text-xs font-600 px-3 py-2 rounded-lg transition-all"
          >
            <Plus size={13} /> Add Keyword
          </button>
          <button
            onClick={handleSync}
            disabled={isSyncing}
            className="flex items-center gap-2 btn-navy"
          >
            {isSyncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {isSyncing ? 'Syncing...' : 'Sync from Elsevier API'}
          </button>
        </div>
      </div>

      {/* Add keyword form */}
      {showAddForm && (
        <div className="bg-white border border-[#e2e6f0] rounded-xl p-4 mb-4 animate-fade-in">
          <h3 className="text-sm font-700 text-[#0f1a35] mb-3">Add New Keyword</h3>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div>
              <label className="text-xs font-600 text-[#4a5578] mb-1 block">Code *</label>
              <input type="text" value={newCode} onChange={e => setNewCode(e.target.value)} placeholder="e.g. CS-ML" className="input-field text-xs" />
            </div>
            <div>
              <label className="text-xs font-600 text-[#4a5578] mb-1 block">Label *</label>
              <input type="text" value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="e.g. Machine Learning" className="input-field text-xs" />
            </div>
            <div>
              <label className="text-xs font-600 text-[#4a5578] mb-1 block">Area *</label>
              <input type="text" value={newArea} onChange={e => setNewArea(e.target.value)} placeholder="e.g. Computer Science" className="input-field text-xs" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleAddKeyword} className="btn-navy text-xs px-4 py-2">Add Keyword</button>
            <button onClick={() => setShowAddForm(false)} className="text-xs text-[#8892a8] hover:text-[#4a5578] px-3 py-2">Cancel</button>
          </div>
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        {[
          { label: 'Total Keywords', value: keywords.length, color: 'text-[#1a2e5a]' },
          { label: 'Active', value: activeCount, color: 'text-emerald-600' },
          { label: 'Inactive', value: inactiveCount, color: 'text-[#8892a8]' },
        ].map((stat) => (
          <div key={`kw-stat-${stat.label}`} className="bg-white border border-[#e2e6f0] rounded-xl p-4 text-center">
            <div className={`text-2xl font-700 tabular-nums ${stat.color}`}>{stat.value}</div>
            <div className="text-xs text-[#8892a8] font-500 mt-0.5">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* API info banner */}
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5">
        <Info size={14} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-blue-800 space-y-0.5">
          <p className="font-700">Elsevier Subject Classifications API</p>
          <p>Endpoint: <code className="font-mono-data bg-blue-100 px-1 rounded">GET https://api.elsevier.com/content/subject/scopus</code></p>
          <p>Keywords are stored in your Supabase database. Deactivated keywords are hidden from student submission forms but remain in existing thesis records.</p>
          <a href="https://dev.elsevier.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-700 hover:text-blue-900 font-600 mt-1">
            <ExternalLink size={10} /> Elsevier Developer Portal
          </a>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8892a8]" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search keywords or codes..." className="input-field pl-8 text-xs" />
        </div>
        <select value={areaFilter} onChange={(e) => setAreaFilter(e.target.value)} className="input-field text-xs max-w-[200px]">
          {uniqueAreas.map(a => <option key={`area-opt-${a}`} value={a}>{a}</option>)}
        </select>
        <div className="flex items-center border border-[#e2e6f0] rounded-lg overflow-hidden text-xs">
          {(['all', 'active', 'inactive'] as const).map((s) => (
            <button key={`status-filter-${s}`} onClick={() => setStatusFilter(s)}
              className={`px-3 py-2 font-600 capitalize transition-all ${statusFilter === s ? 'bg-[#1a2e5a] text-white' : 'bg-white text-[#4a5578] hover:bg-gray-50'}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Grouped keyword list */}
      <div className="space-y-3">
        {Object.entries(groupedByArea).filter(([, kws]) => kws.length > 0).map(([area, kws]) => (
          <div key={`area-group-${area}`} className="bg-white border border-[#e2e6f0] rounded-2xl overflow-hidden">
            <button
              className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-[#1a2e5a]/2 transition-all"
              onClick={() => setExpandedArea(expandedArea === area ? null : area)}
            >
              <div className="flex items-center gap-3">
                <Tag size={15} className="text-[#1a2e5a]" />
                <span className="text-sm font-700 text-[#0f1a35]">{area}</span>
                <span className="text-xs text-[#8892a8] bg-gray-100 px-2 py-0.5 rounded-full">{kws.length} keywords</span>
                <span className="text-xs text-emerald-600 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full">{kws.filter(k => k.is_active).length} active</span>
              </div>
              {expandedArea === area ? <ChevronDown size={15} className="text-[#8892a8]" /> : <ChevronRight size={15} className="text-[#8892a8]" />}
            </button>

            {expandedArea === area && (
              <div className="border-t border-[#e2e6f0] animate-fade-in">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-[#f7f8fc] border-b border-[#e2e6f0]">
                      <th className="px-5 py-2.5 text-left font-700 text-[#4a5578] uppercase tracking-wide">Code</th>
                      <th className="px-5 py-2.5 text-left font-700 text-[#4a5578] uppercase tracking-wide">Label</th>
                      <th className="px-5 py-2.5 text-left font-700 text-[#4a5578] uppercase tracking-wide">Added</th>
                      <th className="px-5 py-2.5 text-left font-700 text-[#4a5578] uppercase tracking-wide">Status</th>
                      <th className="px-5 py-2.5 text-right font-700 text-[#4a5578] uppercase tracking-wide">Toggle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kws.map((kw, idx) => (
                      <tr key={`kw-row-${kw.id}`} className={`border-b border-[#f0f2f8] last:border-0 ${idx % 2 === 0 ? 'bg-white' : 'bg-[#fafbfd]'} hover:bg-[#1a2e5a]/2 transition-colors`}>
                        <td className="px-5 py-2.5"><span className="font-mono-data text-[#8892a8] text-xs">{kw.code}</span></td>
                        <td className="px-5 py-2.5"><span className="font-600 text-[#0f1a35]">{kw.label}</span></td>
                        <td className="px-5 py-2.5"><span className="font-mono-data text-[#8892a8]">{kw.created_at?.slice(0, 10)}</span></td>
                        <td className="px-5 py-2.5">
                          {kw.is_active
                            ? <span className="badge-approved">Active</span>
                            : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-50 text-gray-500 border border-gray-200">Inactive</span>
                          }
                        </td>
                        <td className="px-5 py-2.5 text-right">
                          <button
                            onClick={() => toggleActive(kw)}
                            className={`relative inline-flex items-center h-5 w-9 rounded-full border-2 transition-all duration-200 ${kw.is_active ? 'bg-[#1a2e5a] border-[#1a2e5a]' : 'bg-gray-200 border-gray-200'}`}
                          >
                            <span className={`inline-block w-3 h-3 bg-white rounded-full shadow transition-transform duration-200 ${kw.is_active ? 'translate-x-4' : 'translate-x-0.5'}`} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}

        {Object.values(groupedByArea).every(kws => kws.length === 0) && (
          <div className="flex flex-col items-center justify-center py-16 bg-white border border-[#e2e6f0] rounded-2xl text-center">
            <Tag size={32} className="text-[#c0c8d8] mb-3" />
            <p className="text-sm font-600 text-[#0f1a35] mb-1">No keywords found</p>
            <p className="text-xs text-[#8892a8]">Try adjusting your search or filters.</p>
          </div>
        )}
      </div>
    </div>
  );
}